import React, { useState, useEffect, useMemo } from 'react';
import useWebSocket from '../hooks/useWebSocket';

const StreamingStatus = ({ 
  filters = {}, 
  compact = false, 
  showLogs = true,
  maxMessages = 50,
  title = "Agent Activity"
}) => {
  const [agentStatuses, setAgentStatuses] = useState({});
  const [activeOperations, setActiveOperations] = useState({});
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const clientId = useMemo(() => 
    `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, []
  );

  const { isConnected, messages, sendMessage } = useWebSocket(
    `/ws/agent-status/${clientId}`,
    {
      filters,
      onMessage: (message) => {
        handleWebSocketMessage(message);
      },
      onConnect: () => {
        // Request current agent status when connected
        sendMessage({ type: 'get_agent_status' });
      }
    }
  );

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'agent_log':
        const logData = message.data;
        setAgentStatuses(prev => ({
          ...prev,
          [logData.agent_id]: {
            ...prev[logData.agent_id],
            id: logData.agent_id,
            type: logData.agent_type,
            status: logData.status,
            lastMessage: logData.message,
            lastUpdate: logData.timestamp,
            progress: logData.progress
          }
        }));
        break;
        
      case 'agent_status_response':
        // Update agent statuses from server response
        const agents = {};
        message.agents.forEach(agent => {
          agents[agent.id] = agent;
        });
        setAgentStatuses(agents);
        
        const operations = {};
        message.active_operations.forEach(op => {
          operations[op.id] = op;
        });
        setActiveOperations(operations);
        break;
        
      default:
        break;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'idle': 'bg-gray-100 text-gray-800',
      'starting': 'bg-blue-100 text-blue-800',
      'analyzing': 'bg-purple-100 text-purple-800',
      'processing': 'bg-yellow-100 text-yellow-800',
      'generating': 'bg-indigo-100 text-indigo-800',
      'executing': 'bg-orange-100 text-orange-800',
      'completed': 'bg-green-100 text-green-800',
      'error': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getAgentTypeIcon = (type) => {
    const icons = {
      'ai_agent': '🤖',
      'rca_agent': '🔍',
      'detection_engine': '⚡',
      'rag_agent': '📚',
      'remediation_agent': '🛠️'
    };
    return icons[type] || '🔧';
  };

  const recentMessages = useMemo(() => {
    return messages
      .filter(msg => msg.type === 'agent_log')
      .slice(-maxMessages)
      .reverse();
  }, [messages, maxMessages]);

  if (compact && !isExpanded) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div 
          className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => setIsExpanded(true)}
        >
          <div className="flex items-center space-x-3">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></div>
            <span className="font-medium text-sm">{title}</span>
            <span className="text-xs text-gray-500">
              {Object.keys(agentStatuses).length} agents active
            </span>
          </div>
          <button className="text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></div>
            <h3 className="font-medium text-lg">{title}</h3>
            <span className="text-sm text-gray-500">
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          {compact && (
            <button
              onClick={() => setIsExpanded(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Agent Status Grid */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          {Object.values(agentStatuses).map(agent => (
            <div 
              key={agent.id}
              className={`p-3 border rounded-lg cursor-pointer hover:shadow-md transition-shadow ${
                selectedAgent === agent.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
              }`}
              onClick={() => setSelectedAgent(selectedAgent === agent.id ? null : agent.id)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{getAgentTypeIcon(agent.type)}</span>
                  <span className="font-medium text-sm truncate">{agent.id}</span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agent.status)}`}>
                  {agent.status}
                </span>
              </div>
              
              {agent.progress !== undefined && (
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Progress</span>
                    <span>{agent.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${agent.progress}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-600 truncate">{agent.lastMessage}</p>
              {agent.lastUpdate && (
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(agent.lastUpdate).toLocaleTimeString()}
                </p>
              )}
            </div>
          ))}
        </div>

        {Object.keys(agentStatuses).length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">🤖</div>
            <p className="text-sm">No agent activity detected</p>
            <p className="text-xs text-gray-400">Agents will appear here when they become active</p>
          </div>
        )}
      </div>

      {/* Activity Log */}
      {showLogs && (
        <div className="border-t border-gray-200">
          <div className="p-4">
            <h4 className="font-medium text-sm text-gray-900 mb-3">Recent Activity</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recentMessages.map((message, index) => {
                const data = message.data;
                return (
                  <div key={index} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded">
                    <span className="text-sm">{getAgentTypeIcon(data.agent_type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-900">{data.agent_id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(data.status)}`}>
                          {data.status}
                        </span>
                        {data.progress !== undefined && (
                          <span className="text-xs text-gray-500">{data.progress}%</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{data.message}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(data.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${
                      data.level === 'success' ? 'bg-green-400' :
                      data.level === 'error' ? 'bg-red-400' :
                      data.level === 'warning' ? 'bg-yellow-400' :
                      'bg-blue-400'
                    }`}></div>
                  </div>
                );
              })}
              
              {recentMessages.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamingStatus;